import React, { useMemo } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import { type IProperty, type IMessage, isSupportedSignatureChannel } from 'src/services/api';
import type { ILead } from 'src/services/api';
import { Flex } from 'src/components/flex';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import { analyticsClient } from 'src/services/analytics/analytics-client';
import { AnalyticsEvent } from 'src/services/analytics/events';

interface InviteToScheduleShowingButtonProps {
    lead: ILead;
    property: IProperty;
    messages: IMessage[];
    formattedSignature: string;
    setReplyInput: (replyInput: string) => void;
}

function InviteToScheduleShowingButton({
    lead,
    property,
    messages,
    formattedSignature,
    setReplyInput,
}: InviteToScheduleShowingButtonProps) {
    const { loggedInUser } = useAuthenticatedState();

    const showingSchedulingLink = property.showing_scheduling_link?.link;

    const handleInviteToScheduleShowing = () => {
        analyticsClient.track(AnalyticsEvent.INVITE_TO_TOUR_CLICKED);

        let firstName = ` ${lead.name?.split(' ')[0] || ''}`;
        const leadName = lead.name;
        if (leadName === 'No Name') {
            firstName = '';
        }
        const message = `Hey${firstName}, you can sign up for a tour for ${property.name} using this link: ${showingSchedulingLink}`;
        setReplyInput(message + '\n' + formattedSignature);
    };

    const isInvitedToScheduleShowing = useMemo(() => {
        if (!messages || !showingSchedulingLink) {
            return false;
        }

        const lastMessage = messages[messages.length - 1];
        return lastMessage?.contents.includes(showingSchedulingLink) || false;
    }, [messages, showingSchedulingLink]);

    let disabledReason = '';
    if (!property) {
        disabledReason = 'Lead has no associated property';
    } else if (!showingSchedulingLink) {
        disabledReason = 'Property has no tour scheduling link';
    } else if (isInvitedToScheduleShowing) {
        disabledReason = 'Lead has already been invited';
    } else if (lead.owner !== loggedInUser.id) {
        disabledReason = 'You are not the owner of this lead';
    }

    return (
        <Tooltip title={disabledReason}>
            <div>
                <Button
                    variant="contained"
                    onClick={handleInviteToScheduleShowing}
                    disabled={!!disabledReason}
                    fullWidth
                >
                    Invite to Tour
                </Button>
            </div>
        </Tooltip>

    );
}

interface InviteToApplyButtonProps {
    lead: ILead;
    messages: IMessage[];
    property: IProperty;
    formattedSignature: string;
    setReplyInput: (replyInput: string) => void;
}

function InviteToApplyButton({
    lead,
    messages,
    property,
    formattedSignature,
    setReplyInput,
}: InviteToApplyButtonProps) {
    const { loggedInUser } = useAuthenticatedState();

    const applicationLink = property.application_link?.link;

    const isInvitedToApply = useMemo(() => {
        if (!messages || !applicationLink) {
            return false;
        }

        const lastMessage = messages[messages.length - 1];
        return lastMessage?.contents.includes(applicationLink) || false;
    }, [messages, applicationLink]);

    let disabledReason = '';
    if (isInvitedToApply) {
        disabledReason = 'Lead has already been invited';
    } else if (!property) {
        disabledReason = 'Lead has no associated property';
    } else if (!applicationLink) {
        disabledReason = 'Property has no application link';
    } else if (lead.owner !== loggedInUser.id) {
        disabledReason = 'You are not the owner of this lead';
    }

    const handleInviteToApply = () => {
        analyticsClient.track(AnalyticsEvent.INVITE_TO_APPLY_CLICKED);

        if (!applicationLink) {
            return;
        }

        const message = (`To apply, please fill out your rental application here: ${applicationLink}`);
        setReplyInput(message + '\n' + formattedSignature);
    };

    return (
        <Tooltip title={disabledReason}>
            <div>
                <LoadingButton
                    variant="contained"
                    onClick={handleInviteToApply}
                    disabled={!!disabledReason}
                    fullWidth
                >
                    {isInvitedToApply ? 'Invite Sent' : 'Invite to Apply'}
                </LoadingButton>
            </div>
        </Tooltip>
    );
}

interface Props {
    lead: ILead;
    property: IProperty;
    messages: IMessage[];
    setReplyInput: (replyInput: string) => void;
}

export default function LeadSidebarQuickActions({
    lead,
    property,
    messages,
    setReplyInput,
}: Props) {
    const { channel, userSettings } = useLeadMessagesPageContext();

    const signature = isSupportedSignatureChannel(channel) ? userSettings.signatures[channel] : undefined;
    const isSignatureEnabled = signature?.is_enabled;
    const formattedSignature = isSignatureEnabled ? `\n---\n${signature.text}` : '';

    return (
        <Flex width="100%" sx={{ '& > div': { width: '100%' } }}>
            <InviteToScheduleShowingButton
                lead={lead}
                property={property}
                messages={messages}
                formattedSignature={formattedSignature}
                setReplyInput={setReplyInput}
            />

            <InviteToApplyButton
                lead={lead}
                property={property}
                messages={messages}
                formattedSignature={formattedSignature}
                setReplyInput={setReplyInput}
            />
        </Flex>
    );
}
